import React, { useState, useEffect, useRef, useCallback } from 'react';
import classNames from "classnames";
import ScrollBooster from 'scrollbooster';
import MobilePopup, { MobilePopupFooter, MobilePopupHeader } from '../../components/MobilePopup';
import PriceGraphItem from './PriceGraphItem';
import Counter from '../../components/Counter';
import translate from '../../utilities/translator';
import { IMAGE_CDN } from "../../constants/constants";
import { selectDestinationGraph, setFirstVisibleDate, setPriceGraphNights } from './priceGraphSlice';
import { useDispatch, useSelector } from 'react-redux';
import { parseBoldText, parseDate } from '../../utilities/helpers';
import { getDisplayPrice } from '../../utilities/priceHelper';
import { addDays, parse } from 'date-fns';
import format from './../../utilities/format';
import { pushEvent } from '../../utilities/analytics';
import { PRICE_GRAPH_CHANGE_NIGHTS } from '../../constants/analyticEvents';
import { selectDateFilter, selectMonthAhead } from '../mainFilter/selectors';
import { DESKTOP_GRAPH_ITEM_WIDTH } from './constants';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

export default function PriceGraphMobile({
  dates,
  applyDates,
  nightsLimit,
  destination,
  filterDate,
}) {
  const dispatch = useDispatch();
  const viewport = useRef(null);
  const monthAhead = useSelector(selectMonthAhead);
  const [isOpen, setIsOpen] = useState(false);
  const [activeDate, setActiveDate] = useState(dates[1]);
  const [activeCandleIndex, setActiveCandleIndex] = useState(1);
  const [datesList, setDatesList] = useState([]);
  const { nights, pending: graphLoading } = useSelector(selectDestinationGraph);
  const [scrollbooster, setScrollBooster] = useState(null);
  const [visibleDates, setVisibleDates] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 0 });
  const isRtl = document.dir === 'rtl';

  useBodyScrollLock({ condition: isOpen, mobileOnly: true });

  const {
    startDate,
  } = useSelector(selectDateFilter);

  const formattedDates = [
    ...new Set(
      dates.map((date) =>
        format(parse(date.start, 'dd/MM/yyyy', new Date()), 'MM/yyyy')
      )
    ),
  ];

  const generateDatesArray = useCallback(() => {
    const itemWidth = document.querySelector('.price-graph__item')?.clientWidth;
    const wrapWidth = viewport.current?.offsetWidth;
    const itemsToAppend = Math.round((wrapWidth / itemWidth) - 2);

    const appendItems = count => {
      let emptyItems = [];
      for (let i = 1; i <= count; i++) {
        emptyItems.push({
          start: '01/01/1970',
          end: '01/01/1970',
          avgPrice: 0,
        })
      }

      return emptyItems;
    }

    const newDates = [...appendItems(1), ...dates, ...appendItems(itemsToAppend)];
    setDatesList(newDates);
  }, [dates]);

  const onScrollUpdate = useCallback((props) => {
    const { isMoving, position } = props;
    let activeIndex = Math.floor(((Math.abs(position.x) + 21.5) / 43));
    setActiveDate(dates[activeIndex]);
    setActiveCandleIndex(activeIndex + 1);

    if (!isMoving && isOpen) {
      const nodeWidth = document.querySelector('.price-graph__item')?.clientWidth;
      const visibleElements = Math.round(document.querySelector('.price-graph .list-wrap')?.clientWidth / nodeWidth);
      const startElement = Math.round(Math.abs(position.x) / nodeWidth);
      setVisibleDates(dates.slice(startElement, startElement + visibleElements));
      dispatch(setFirstVisibleDate(dates[activeIndex].start));
    }
  }, [dates, dispatch, isOpen])

  useEffect(() => {
    if (!isOpen) setScrollBooster(null)

    if(!scrollbooster && isOpen) {
      setScrollBooster(
        new ScrollBooster({
          viewport: viewport.current,
          scrollMode: 'transform',
          direction: 'horizontal',
          bounce: false,
          friction: 0.5,
          rtl: document.dir === 'rtl',
          onUpdate: onScrollUpdate
        })
      );
    }
  }, [isOpen, scrollbooster, onScrollUpdate])

  useEffect(() => {
    const nodeWidth = DESKTOP_GRAPH_ITEM_WIDTH;
    const index = dates.indexOf(
      dates.filter((item) => {
        const startDateArray = item.start.split('/');
        return (
          +startDateArray[1] === +filterDate.month &&
          +startDateArray[0] === +filterDate.date
        );
      })[0]
    );
    if (!scrollbooster) return;

    scrollbooster.setPosition({
      x: isRtl ? -(nodeWidth * index + 2) : nodeWidth * index + 2,
      y: 0,
    });
  }, [filterDate, scrollbooster, isRtl]);

  useEffect(() => {
    if (scrollbooster) {
      scrollbooster.updateOptions({
        onUpdate: onScrollUpdate
      })
    }
  }, [onScrollUpdate, scrollbooster])

  useEffect(() => {
    if (isOpen) {
      generateDatesArray()
    };
  }, [dates, generateDatesArray, isOpen])

  useEffect(() => {
    if (scrollbooster) {
      scrollbooster.updateMetrics();
    };
  }, [datesList, isOpen, scrollbooster, viewport])

  useEffect(() => {
    if (visibleDates.length !== 0) {
      const nonZeroPrices = visibleDates.filter((i) => i.avgPrice !== 0);
      if (nonZeroPrices.length > 0) {
        const min = nonZeroPrices.reduce(
          (min, currentValue) => Math.min(min, currentValue.avgPrice),
          nonZeroPrices.filter((i) => i.avgPrice !== 0)[0].avgPrice
        );
        const max = nonZeroPrices.reduce(
          (min, currentValue) => Math.max(min, currentValue.avgPrice),
          nonZeroPrices.filter((i) => i.avgPrice !== 0)[0].avgPrice
        );

        setPriceRange({ min, max });
      }
    }
  }, [visibleDates])

  const getProgress = useCallback((value) => {
    const range = (priceRange.max - priceRange.min);
    if (range === 0) return 0;

    return (52 / range) * (value - priceRange.min) + 10;
  }, [priceRange]);

  const changeNightsOnClick = (nights) => {
    dispatch(setPriceGraphNights(nights));
    pushEvent(PRICE_GRAPH_CHANGE_NIGHTS, nights);
  };

  const formatDateRange = () => {
    let startRange = parseDate(activeDate.start);
    let endRange = addDays(parseDate(activeDate.start), nights);

    if (format(startRange, 'M') === format(endRange, 'M')) {
      return `${format(startRange, 'd')} - ${format(endRange, 'd MMM')}`;
    }

    return `${format(startRange, 'd MMM')} - ${format(endRange, 'd MMM')}`;
  };

  const onApplyClick = useCallback(() => {
    const start = parseDate(activeDate.start);
    const end = addDays(parseDate(activeDate.start), nights);
    applyDates({ start, end });
    setIsOpen(false);
  }, [activeDate, nights]);

  useEffect(() => {
    if (window.innerWidth < 768) {
      window.addEventListener('popstate', () => {
        setIsOpen(false);
      });
    }
  }, []);

  const handleOpen = () => {
    setIsOpen(true);
    window.history.pushState({ type: 'something' }, null, window.location.href);
  };

  const graphMonthLabel = startDate
    ? format(parse(startDate, 'dd/MM/yyyy', new Date()), 'MMMM')
    : format(parse(formattedDates[0], 'MM/yyyy', new Date()), 'MMMM')

  const graphSubtitle = parseBoldText(translate('holidayFinder.priceGraph.subtitle_desktop', {
    nights,
    destination: destination.name,
    month: graphMonthLabel,
  }));

  const calculateDaysToShow = () => {
    const getDays = (year, month) => {
      return new Date(year, month, 0).getDate();
    };

    let changedDate = new Date()
    let days = getDays(changedDate.getFullYear(), changedDate.getMonth()) - new Date().getDate() - 1;

    for (let index = 1; index < monthAhead; index++) {
      changedDate.setMonth(changedDate.getMonth() + 1)
      days = days + getDays(changedDate.getFullYear(), changedDate.getMonth())
    }
    return days
  }

  calculateDaysToShow()

  return (
    <div className="col-lg-12">
      <div className="price-graph-mobile-preview">
        <div className="price-graph-mobile__description">
          <div>{graphSubtitle}</div>
          <img src={`${IMAGE_CDN}/price-graph.png`} alt="" />
        </div>
        <button className="open-graph" onClick={handleOpen}>{translate('holidayFinder.priceGraph.open')}</button>
      </div>
      {isOpen && (
        <MobilePopup onClose={() => setIsOpen(false)}>
          <MobilePopupHeader onClose={() => setIsOpen(false)}>
            <div className="price-graph-mobile__title">{translate('holidayFinder.priceGraph.title_mobile')}</div>
          </MobilePopupHeader>
          <div className="mobile-popup__body price-graph-mobile">
            <div className="price-graph-mobile__text">{graphSubtitle}</div>
            <div className="price-graph-mobile__details-wrap">
              <div className="price-graph-mobile__details-content">
              <div className="modify-dates__date">{format(parseDate(activeDate.start), 'EEE, MMMM d')}</div>
              <div className="modify-dates__footer">
                <div className="modify-dates__price">
                  <span>{translate('holidayFinder.priceGraph.item_from')}</span>
                  <div>{getDisplayPrice(activeDate.avgPrice)}</div>
                </div>
                <div>
                  <div className="modify-dates__nights">{nights} {translate('holidayFinder.priceGraph.item_nights_plural', { count: nights })}</div>
                  <span className="modify-dates__period">{formatDateRange()}</span>
                </div>
                <Counter
                  showValue={false}
                  value={nights}
                  min={nightsLimit.min}
                  max={nightsLimit.max}
                  onChange={changeNightsOnClick}
                />
              </div>
            </div>
            </div>
            <div className={classNames({ 'price-graph': true, 'price-graph--loading': graphLoading })}>
              <div className="carousel">
                <div className="list-wrap" ref={viewport}>
                  <div className="list">
                    <div className="price-graph__slide">
                      <div className="list">
                        {datesList.slice(0,calculateDaysToShow()).map((date, index) => (
                          <PriceGraphItem
                            key={index}
                            date={date}
                            getProgress={getProgress}
                            scrollbooster={scrollbooster}
                            onApply={applyDates}
                            isActive={activeCandleIndex === index}
                            highlightActive
                            nights={nights}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {graphLoading && <div className="price-graph__loader" />}
            </div>
          </div>

          <MobilePopupFooter>
            <button className="btn btn-link" onClick={() => setIsOpen(false)}>{translate('holidayFinder.buttons.cancel')}</button>
            <button className="btn" onClick={onApplyClick}>{translate('holidayFinder.buttons.Apply')}</button>
          </MobilePopupFooter>
        </MobilePopup>
      )}
    </div>
  )
}
